import { useState, useEffect, useCallback } from "react";
import { Tooltip, Drawer, Skeleton } from "antd";
import {
  FileDoneOutlined, DownloadOutlined, MailOutlined, HistoryOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  fetchInvoices, fetchInvoiceSendLog, openInvoice,
} from "../../../services/invoiceService";
import SendInvoiceModal from "../../../invoices/SendInvoiceModal";
import {
  cardStyle, inr, num, dateTime, relative, chip, TONE, titleCase,
  PanelHeader, PanelTable, PanelSkeleton, PanelEmpty, PanelError, usePanelData,
} from "./shared";

/**
 * Every document this customer has been issued, of every kind.
 *
 * Nothing here branches on the document type. Each row carries its own label,
 * download URL and send endpoint, so when the backend starts emitting proposals
 * or refunds they appear in this table — filter included — without a line
 * changing here.
 */

/* Colours only — a kind missing from here still lists, chips and sends; it
   just renders grey. Nothing about a document's behaviour is looked up. */
const TYPE_TONE = {
  subscription: TONE.violet,
  wallet: TONE.green,
  ai_credit: TONE.amber,
};

const PAYMENT_TONE = {
  paid: TONE.green,
  partial: TONE.amber,
  unpaid: TONE.red,
};

/** Who was sent what, and whether it landed. */
const SendLogDrawer = ({ userId, open, onClose }) => {
  const [state, setState] = useState({ rows: null, loading: false, error: null });

  const load = useCallback(() => {
    setState({ rows: null, loading: true, error: null });
    fetchInvoiceSendLog(userId)
      .then((res) => setState({ rows: res?.data || [], loading: false, error: null }))
      .catch((error) => setState({ rows: null, loading: false, error }));
  }, [userId]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      afterOpenChange={(o) => { if (o) load(); }}
      width={640}
      title={<span style={{ fontWeight: 700, fontSize: 15 }}>Send history</span>}
      styles={{ body: { padding: 16, background: "#f8fafc" } }}
    >
      {state.loading && <Skeleton active paragraph={{ rows: 6 }} />}
      {state.error && <PanelError error={state.error} onRetry={load} />}
      {!state.loading && !state.error && (state.rows || []).length === 0 && (
        <PanelEmpty message="Nothing has been emailed to this customer yet." />
      )}
      {!state.loading && !state.error && (state.rows || []).map((r, i) => (
        <div
          key={r.id ?? i}
          style={{
            background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0",
            padding: "13px 15px", marginBottom: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 5 }}>
            {chip(titleCase(r.invoice_type), TYPE_TONE[r.invoice_type] || TONE.slate)}
            <span style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>{r.invoice_number || "—"}</span>
            <span style={{ fontSize: 13, color: "#475569", fontVariantNumeric: "tabular-nums" }}>{inr(r.amount)}</span>
          </div>
          <div style={{ fontSize: 12.5, color: "#64748b", wordBreak: "break-all" }}>
            to <b style={{ color: "#334155" }}>{r.sent_to || "—"}</b>
            {r.sent_by ? <> · by {r.sent_by}</> : null}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 7, flexWrap: "wrap" }}>
            {chip(titleCase(r.status), /fail|error|bounce/i.test(String(r.status)) ? TONE.red : TONE.green)}
            <Tooltip title={dateTime(r.created_at)}>
              <span style={{ fontSize: 11.5, color: "#94a3b8" }}>{relative(r.created_at)}</span>
            </Tooltip>
          </div>
        </div>
      ))}
    </Drawer>
  );
};

const InvoicesTab = ({ userId, active, customerEmail = "" }) => {
  const [type, setType] = useState("all");

  /* Every document kind this customer has been seen to have, accumulated as
     responses arrive. Nothing is listed here in code — a kind the backend
     starts emitting becomes a chip on its own.
     Accumulated rather than read off the current page because filtering to one
     kind leaves only that kind in the rows, which would collapse the filter to
     the option already chosen and strand the reader there. */
  const [kinds, setKinds] = useState([]);

  // A different customer has a different set; nothing carries over.
  useEffect(() => { setKinds([]); setType("all"); }, [userId]);

  const remember = useCallback((incoming) => {
    setKinds((prev) => {
      const next = [...prev];
      incoming.forEach((t) => {
        if (t.key && !next.some((k) => k.key === t.key)) next.push(t);
      });
      return next.length === prev.length ? prev : next;
    });
  }, []);

  /* The filter is part of the request, not a pass over loaded rows — the count
     under the pager has to describe the whole filtered set. Folding it into the
     fetcher keeps the panel hook unchanged. */
  const fetcher = useCallback(
    (uid, page, limit) => fetchInvoices(uid, page, limit, type),
    [type],
  );

  /* `key` is what makes the filter actually refetch. The hook only watches the
     customer and the search term, so without naming the type here a filter
     click would change the request and show the previous answer. */
  const p = usePanelData(fetcher, userId, active, { perPage: 15, key: type });

  /* Two sources, both optional: the list the API publishes, and whatever the
     rows themselves carry. Either alone is enough. */
  useEffect(() => {
    if (!p.data) return;
    const fromApi = (Array.isArray(p.data.types) ? p.data.types : []).map((t) => (
      typeof t === "string"
        ? { key: t, label: titleCase(t) }
        : { key: t.type, label: t.label || titleCase(t.type) }
    ));
    const fromRows = [...new Set((p.rows || []).map((r) => r.type).filter(Boolean))]
      .map((t) => {
        const row = p.rows.find((r) => r.type === t);
        return { key: t, label: row?.type_label || titleCase(t) };
      });
    remember([...fromApi, ...fromRows]);
  }, [p.data, p.rows, remember]);

  const [sending, setSending] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [opening, setOpening] = useState(null);

  const download = async (row) => {
    setOpening(row.ref_id);
    try {
      await openInvoice(row.download_url);
    } catch (err) {
      toast.error(err.message || "This document could not be opened");
    } finally {
      setOpening(null);
    }
  };

  if (p.loading && !p.data) return <PanelSkeleton />;
  if (p.error) return <PanelError error={p.error} onRetry={p.reload} />;

  const filters = [{ key: "all", label: "All" }, ...kinds];

  const columns = [
    {
      title: "Type", dataIndex: "type_label", width: 165,
      /* The label comes from the row, so an unknown type still reads properly
         instead of falling back to a code. Only the colour is looked up. */
      render: (v, r) => chip(v || titleCase(r.type), TYPE_TONE[r.type] || TONE.slate),
    },
    {
      title: "Number", dataIndex: "invoice_number", width: 200,
      render: (v) => <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 12.5 }}>{v || "—"}</span>,
    },
    {
      title: "Title", dataIndex: "title", width: 220,
      render: (v) => (
        <Tooltip title={v}>
          <span style={{ fontSize: 12.5, color: "#475569" }}>{v || "—"}</span>
        </Tooltip>
      ),
    },
    {
      title: "Amount", dataIndex: "amount", width: 125, align: "right",
      render: (v) => <span style={{ fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>{inr(v)}</span>,
    },
    {
      title: "Date", dataIndex: "date", width: 130,
      render: (v) => (
        <Tooltip title={dateTime(v)}>
          <span style={{ fontSize: 12.5, color: "#475569", whiteSpace: "nowrap" }}>{relative(v)}</span>
        </Tooltip>
      ),
    },
    {
      title: "Status", dataIndex: "payment_status", width: 110,
      render: (v) => chip(v || "—", PAYMENT_TONE[String(v).toLowerCase()] || TONE.slate),
    },
    {
      title: "Last sent", key: "sent", width: 175,
      /* Two facts in one column: whether it ever went, and when it last did.
         "Not sent yet" is the one people scan for. */
      render: (_, r) => {
        if (!r.last_sent_at) return <span style={{ color: "#cbd5e1", fontSize: 12 }}>Not sent yet</span>;
        const times = Number(r.sent_count) || 1;
        return (
          <Tooltip title={dateTime(r.last_sent_at)}>
            <span style={{ fontSize: 12.5, color: "#475569", whiteSpace: "nowrap" }}>
              {times > 1 && <b>{num(times)}× · </b>}
              last {relative(r.last_sent_at)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions", key: "actions", width: 130, fixed: "right",
      render: (_, r) => (
        <div style={{ display: "inline-flex", gap: 6 }}>
          <Tooltip title="Open the PDF">
            <button
              onClick={() => download(r)}
              disabled={opening === r.ref_id}
              style={{
                width: 30, height: 30, borderRadius: 8, border: "1px solid #ddd6fe",
                background: "#f5f3ff", color: "#6366f1", cursor: "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12,
              }}
            >
              <DownloadOutlined spin={opening === r.ref_id} />
            </button>
          </Tooltip>
          <Tooltip title="Email this to the customer">
            <button
              onClick={() => setSending(r)}
              style={{
                width: 30, height: 30, borderRadius: 8, border: "1px solid #bfdbfe",
                background: "#eff6ff", color: "#2563eb", cursor: "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12,
              }}
            >
              <MailOutlined />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div style={cardStyle}>
      <PanelHeader
        icon={<FileDoneOutlined />}
        title="Invoices & receipts"
        subtitle="Every document issued to this customer"
        iconBg="linear-gradient(135deg,#4f46e5,#6366f1)"
        right={(
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 6 }}>
              {filters.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setType(f.key)}
                  style={{
                    padding: "4px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                    cursor: "pointer", whiteSpace: "nowrap",
                    border: `1px solid ${type === f.key ? "#ddd6fe" : "#e2e8f0"}`,
                    background: type === f.key ? "#f5f3ff" : "#fff",
                    color: type === f.key ? "#6366f1" : "#64748b",
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setLogOpen(true)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 13px",
                borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff",
                color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}
            >
              <HistoryOutlined /> Send history
            </button>
          </div>
        )}
      />
      <PanelTable
        columns={columns}
        rows={p.rows}
        loading={p.loading}
        page={p.page}
        total={p.total}
        perPage={p.perPage}
        onPage={p.goToPage}
        rowKey="ref_id"
        empty="No invoices for this customer yet."
        scrollX={1430}
      />

      <SendInvoiceModal
        open={Boolean(sending)}
        invoice={sending}
        userId={userId}
        defaultEmail={customerEmail}
        onClose={() => setSending(null)}
        /* The row's own "last sent" is what just changed, so the list is
           re-read rather than patched — the server owns that count. */
        onSent={p.reload}
      />

      <SendLogDrawer userId={userId} open={logOpen} onClose={() => setLogOpen(false)} />
    </div>
  );
};

export default InvoicesTab;
